// rum.config.ts
import type { RumInitConfiguration } from "@datadog/browser-rum";
import { reactPlugin } from "@datadog/browser-rum-react/cjs/domain/reactPlugin";

import { isLocalhost } from "@/constant";

import { scrubRumEvent } from "./rum-privacy";
import { RUM_APP } from "./rum-service";

export { isRumEnabled } from "./rum-service";

const tracingPropagators: ["datadog", "tracecontext"] = ["datadog", "tracecontext"];
// moneymaxservices.com itself and any of its subdomains
const moneymaxTracingUrl = {
  match: /^https:\/\/(?:[a-z0-9-]+\.)*moneymaxservices\.com(?::\d+)?(?:\/|$)/i,
  propagatorTypes: tracingPropagators,
};
const localhostTracingUrl = {
  match: /^https?:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?(?:\/|$)/i,
  propagatorTypes: tracingPropagators,
};

const rumConfig: RumInitConfiguration = {
  applicationId: process.env.DD_RUM_APPLICATION_ID || "",
  clientToken: process.env.DD_RUM_CLIENT_TOKEN || "",
  site: process.env.DD_SITE || "us3.datadoghq.com",
  // Local runs never mix with SIT/UAT sessions.
  env: isLocalhost ? "local" : process.env.APP_ENV,
  version: process.env.GIT_RELEASE_TAG,
  startSessionReplayRecordingManually: true,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 100,
  trackUserInteractions: true,
  trackResources: true,
  trackLongTasks: true,
  trackAnonymousUser: false,
  defaultPrivacyLevel: "allow",
  enablePrivacyForActionName: true,
  beforeSend: (event, context) => scrubRumEvent(event, context),
  plugins: [reactPlugin({ router: true })],
};

// Same "<app>:<domain>" service as retail (retail/hq/risk-screening) and region admin (admin).
export function getRumConfig(): RumInitConfiguration {
  // Trimmed like the other DD_RUM_* values: a stray space or newline in the
  // pipeline variable must not end up in the ID, and blank means "off".
  const remoteConfigurationId = process.env.DD_RUM_REMOTE_CONFIGURATION_ID?.trim();
  return {
    ...rumConfig,
    allowedTracingUrls: isLocalhost
      ? [moneymaxTracingUrl, localhostTracingUrl]
      : [moneymaxTracingUrl],
    service: `${RUM_APP}:${getDomainName()}`,
    // Settings managed in Datadog (RUM > Remote Configuration) on top of these.
    ...(remoteConfigurationId && { remoteConfiguration: { id: remoteConfigurationId } }),
  };
}

function getDomainName(): string {
  return isLocalhost
    ? new URL(String(process.env.AUTH_API_BASE_URL)).hostname
    : window.location.host;
}
